/* ETOILE mobile swatch scroll isolation */
(() => {
  const marker = 'etoileMobileSwatchScroll';
  const selector = '.product-grid-item__swatch';
  const scrollableClass = 'swatch-scrollable';
  const boundRows = new WeakSet();

  // A row only needs drag-scroll (and the touch-action override that comes
  // with it) when its swatches don't fit the available width. Applying the
  // scrollable treatment unconditionally to EVERY row — including ones with
  // just 1-2 colours that fit easily — was the actual bug: once a container
  // has touch-action: pan-x and overflow-x: auto, mobile Safari's own gesture
  // recognizer (not our JS) treats any tap with the slightest horizontal
  // finger drift as the start of a scroll and swallows the click before any
  // JS handler ever sees it. Rows that don't overflow get no touch-action
  // override at all, so a tap on a swatch is just a normal tap.
  const measure = (row) => {
    const needsScroll = row.scrollWidth > row.clientWidth + 1;
    row.classList.toggle(scrollableClass, needsScroll);
  };

  const bindRow = (row) => {
    if (boundRows.has(row)) return;
    boundRows.add(row);

    const keepGestureInRow = (event) => {
      if (!row.classList.contains(scrollableClass)) return;
      event.stopPropagation();
    };

    row.addEventListener('touchstart', keepGestureInRow, { passive: true });
    row.addEventListener('touchmove', keepGestureInRow, { passive: true });
    row.addEventListener('pointerdown', keepGestureInRow, { passive: true });
    row.addEventListener('pointermove', (event) => {
      if (!row.classList.contains(scrollableClass)) return;
      event.stopImmediatePropagation();
    }, { capture: true, passive: true });

    // Re-measure every time the row's visibility toggles (showswatchs), since
    // it reports scrollWidth 0 while visibility: hidden and the swatch count
    // is static after that. Also re-measure on resize (orientation change,
    // etc.) since what fits at one width may not fit at another.
    new MutationObserver(() => measure(row)).observe(row, {
      attributes: true,
      attributeFilter: ['class'],
    });
    measure(row);
  };

  const bindRows = (root = document) => {
    if (root.matches?.(selector)) bindRow(root);
    root.querySelectorAll?.(selector).forEach(bindRow);
  };

  if (!document.documentElement.dataset[marker]) {
    document.documentElement.dataset[marker] = 'true';

    const style = document.createElement('style');
    style.textContent = [
      // The theme's own base CSS sets touch-action: pan-x unconditionally on
      // EVERY product-grid-item-swatch element (not just ones that scroll).
      // That alone is enough for mobile Safari to treat any tap-with-drift as
      // a pan gesture and swallow the click, so it has to be explicitly reset
      // back to auto for rows we've determined don't need to scroll.
      `product-grid-item-swatch.product-grid-item__swatch:not(.${scrollableClass}) {`,
      '  touch-action: auto !important;',
      '}',
      '@media (max-width: 767px) {',
      `  .product-grid-item__swatch.${scrollableClass} {`,
      '    min-width: 0;',
      '    max-width: 100%;',
      '    overflow-x: auto !important;',
      '    overflow-y: hidden !important;',
      '    -webkit-overflow-scrolling: touch;',
      '    overscroll-behavior-x: contain;',
      '    touch-action: pan-x !important;',
      '    scrollbar-width: none;',
      '  }',
      `  .product-grid-item__swatch.${scrollableClass}::-webkit-scrollbar { display: none; }`,
      `  .product-grid-item__swatch.${scrollableClass} > .swatch__button { flex: 0 0 auto; }`,
      '  .product-grid-item__swatch:not(.' + scrollableClass + ') {',
      '    flex-wrap: wrap;',
      '  }',
      '}',
    ].join('\n');
    document.head.appendChild(style);

    bindRows();
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) bindRows(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    window.addEventListener('resize', () => {
      document.querySelectorAll(selector).forEach(measure);
    });
  }
})();
